"use client";

import { createContext, type ReactNode, useContext, useMemo } from "react";
import type { PhoneAuthClientFlags } from "@/lib/identity/launch-flags";

/**
 * The launch flags, as the client sees them.
 *
 * Mounted once in the root layout from `phoneAuthClientFlags()`, which is a
 * synchronous env read, so this adds no suspense boundary over `children`
 * (which cacheComponents forbids in a layout).
 *
 * The client uses these ONLY to decide what to render — hide a sign-in method
 * that has no provider behind it, or skip a payment round trip that is certain
 * to be refused. Every flag is independently enforced on the server; hiding a
 * button is not a rollout, because the endpoint stays reachable either way.
 */

const DEFAULT_FLAGS: PhoneAuthClientFlags = {
  phoneOtpEnabled: false,
  verifiedPhonePaymentGate: false,
  bookingEmailEnabled: false,
};

const LaunchFlagsContext = createContext<PhoneAuthClientFlags>(DEFAULT_FLAGS);

export function LaunchFlagsProvider({
  flags,
  children,
}: {
  flags: PhoneAuthClientFlags;
  children: ReactNode;
}) {
  // Memoized on the primitives rather than on the object: the layout re-creates
  // the object on every render, which would otherwise re-render every consumer.
  const value = useMemo(
    () => flags,
    [
      flags.phoneOtpEnabled,
      flags.verifiedPhonePaymentGate,
      flags.bookingEmailEnabled,
      flags,
    ],
  );

  return (
    <LaunchFlagsContext.Provider value={value}>
      {children}
    </LaunchFlagsContext.Provider>
  );
}

/**
 * Defaults to everything off when no provider is present, so a component
 * rendered outside the tree (a test or Storybook) cannot accidentally promise
 * a disabled feature.
 */
export function useLaunchFlags(): PhoneAuthClientFlags {
  return useContext(LaunchFlagsContext);
}
